// src/pages/admin/PerformanceAdminView.jsx
import { useMemo, useState } from "react";
import { DataPageCard, PaginatedTable, CheckboxField } from "@common";

import {
  performanceOverviewColumns,
  practitionerOverviewColumns,
  costCollectionOverviewColumns,
} from "@utils";

export default function PerformanceAdminView({
  date,
  data,
  isLoading,
  error,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  const [showPractitioner, setShowPractitioner] = useState(false);
  const [showCostCollection, setShowCostCollection] = useState(false);

  // Process data based on selected view
  const allDisplayData = useMemo(() => {
    if (!data?.results) return [];

    const result = [];

    data.results.forEach((clinicData) => {
      if (showPractitioner) {
        // Show practitioner breakdown for each clinic
        if (clinicData.practitionerBreakdown?.length > 0) {
          clinicData.practitionerBreakdown.forEach((pract) => {
            result.push({
              clinic: clinicData.clinic,
              practitioner_id: pract.practitioner_id,
              practitioner_name: pract.practitioner_name,
              practitioner_type: pract.practitioner_type,
              category: pract.category,
              collection: pract.practitioner_collection,
              total_costs:
                (pract.lab_costs || 0) +
                (pract.material_costs || 0) +
                (pract.invisalign_costs || 0),
              production: pract.practitioner_production,
              cash: 0,
              debit: 0,
              mastercard: 0,
              visa: 0,
              amex: 0,
              checks: 0,
              insurance_check: 0,
              personal_eft: 0,
              insurance_eft: 0,
              other: 0,
              lab_costs: pract.lab_costs || 0,
              invisalign_costs: pract.invisalign_costs || 0,
              material_costs: pract.material_costs || 0,
              hours_worked: pract.hours_worked || 0,
            });
          });
        }
      } else if (showCostCollection) {
        // Show cost and collection breakdown
        const cc = clinicData.costAndCollection || {};
        result.push({
          clinic: clinicData.clinic,
          cash: cc.collection_cash || 0,
          debit: cc.collection_debit || 0,
          mastercard: cc.collection_mastercard || 0,
          visa: cc.collection_visa || 0,
          amex: cc.collection_amex || 0,
          checks: cc.collection_checks || 0,
          insurance_check: cc.collection_insurance_check || 0,
          personal_eft: cc.collection_personal_eft || 0,
          insurance_eft: cc.collection_insurance_eft || 0,
          other: cc.collection_other || 0,
          lab_costs: cc.lab_costs || 0,
          invisalign_costs: cc.invisalign_costs || 0,
          material_costs: cc.material_costs || 0,
          hours_worked: cc.hours_worked || 0,
          collection: cc.collection_total || 0,
          total_costs: (cc.lab_costs || 0) + (cc.material_costs || 0) + (cc.invisalign_costs || 0),
          production: clinicData.performance?.production || 0,
        });
      } else {
        // Show performance summary
        const perf = clinicData.performance || {};
        result.push({
          clinic: clinicData.clinic,
          collection: perf.collection || 0,
          total_costs: perf.total_cost || 0,
          production: perf.production || 0,
        });
      }
    });

    return result;
  }, [data, showPractitioner, showCostCollection]);

  const displayData = allDisplayData;
  const totalItems = displayData.length;

  // Dynamic columns - add to base columns when checkboxes are selected
  const columns = useMemo(() => {
    const baseColumns = performanceOverviewColumns();

    if (showPractitioner && showCostCollection) {
      return [...baseColumns, ...practitionerOverviewColumns(), ...costCollectionOverviewColumns()];
    } else if (showPractitioner) {
      return [...baseColumns, ...practitionerOverviewColumns()];
    } else if (showCostCollection) {
      return [...baseColumns, ...costCollectionOverviewColumns()];
    }

    return baseColumns;
  }, [showPractitioner, showCostCollection]);

  const isEmpty = !date || displayData.length === 0;

  return (
    <DataPageCard
      title="Performance at a Glance"
      className="mb-5"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      loadingMessage="Loading performance data..."
      emptyMessage={
        !date
          ? "Please select a date to view performance data"
          : `No performance data available for ${date}`
      }
      showLoadingSpinner={true}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-2 pt-0">
        <CheckboxField
          label="Show Practitioner Breakdown"
          checked={showPractitioner}
          onChange={setShowPractitioner}
        />
        <CheckboxField
          label="Show Cost & Collection Breakdown"
          checked={showCostCollection}
          onChange={setShowCostCollection}
        />
      </div>

      <PaginatedTable
        mode="client"
        columns={columns}
        data={displayData}
        rowKey={(r, i) => `${r.clinic}-${i}`}
        cellClassName="font-semibold text-palette-slate-primary"
        page={page}
        pageSize={pageSize}
        total={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </DataPageCard>
  );
}
